import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// Global "what's happening right now" status — lives above <Routes> in
// App.jsx (same slot pattern as SellviaCleanupJobContext) so it survives
// navigating between admin pages. Unlike SellviaCleanupJobContext (which
// polls a backend job record for the Pending-tab Manual/Auto cleanup run),
// this one is purely client-side: whichever page kicks off a "Process
// Order" (Approved tab) or "Sync Live"/"Sync All" action just pushes its
// own progress into it directly as the loop runs.
const SellviaActivityContext = createContext(null);

export const SellviaActivityProvider = ({ children }) => {
    // { type: 'sync' | 'process', label, message, current, total,
    //   successCount, failCount, status: 'running' | 'done' | 'error' }
    const [activity, setActivity] = useState(null);
    const clearTimerRef = useRef(null);

    const startActivity = useCallback((init) => {
        if (clearTimerRef.current) { clearTimeout(clearTimerRef.current); clearTimerRef.current = null; }
        setActivity({ current: 0, total: 0, successCount: 0, failCount: 0, message: '', status: 'running', ...init });
    }, []);

    const updateActivity = useCallback((patch) => {
        setActivity((prev) => (prev ? { ...prev, ...patch } : prev));
    }, []);

    // Marks the activity done/error and auto-clears the banner a few seconds
    // later — same "close automatically" behaviour as the cleanup banner.
    const finishActivity = useCallback((patch = {}, autoClearMs = 5000) => {
        setActivity((prev) => (prev ? { ...prev, status: 'done', ...patch } : prev));
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(() => setActivity(null), autoClearMs);
    }, []);

    return (
        <SellviaActivityContext.Provider value={{ activity, startActivity, updateActivity, finishActivity }}>
            {children}
        </SellviaActivityContext.Provider>
    );
};

export const useSellviaActivity = () => useContext(SellviaActivityContext);
